import { useMemo, useState } from "react";
import { usePageState } from "@/hooks/usePageState";
import { Loader2, Upload, Download, AlertTriangle } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/common";
import { LiveBadge } from "@/components/LiveBadge";
import { BookingTable } from "@/components/BookingTable";
import { BookingFilters, EMPTY_FILTERS } from "@/components/BookingFilters";
import { useFetch } from "@/hooks/useFetch";
import { useBookingActions } from "@/hooks/useBookingActions";
import { money, todayISO } from "@/lib/format";
import { bookingsToCsv, downloadCsv, csvFilename } from "@/lib/csv";

const Total = ({ id, label, value, tone = "text-slate-900" }) => (
  <div className="rounded-lg border border-slate-200 bg-white px-4 py-3" data-testid={`totals-${id}`}>
    <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
    <div className={`mt-0.5 font-mono text-lg font-bold ${tone}`}>{value}</div>
  </div>
);

export default function BookingsPage() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [saved, setSaved] = usePageState("filters", EMPTY_FILTERS);
  const [filters, setFiltersRaw] = useState(() => sp.get("flagged") ? { ...EMPTY_FILTERS, flagged: "1", scope: "all" }
    : sp.get("date") ? { ...EMPTY_FILTERS, scope: "all", date_from: sp.get("date"), date_to: sp.get("date") } : saved);
  const setFilters = (f) => { setFiltersRaw(f); setSaved(f); };
  const params = useMemo(() => {
    const p = { ...filters, today: todayISO() };
    if (filters.date_from || filters.date_to) p.scope = "all";
    return Object.fromEntries(Object.entries(p).filter(([, v]) => v));
  }, [filters]);
  const { data, loading, reload, updatedAt } = useFetch("/bookings", params);
  const actions = useBookingActions(() => reload());
  const bookings = data || [];
  const live = bookings.filter((b) => b.status !== "Cancelled");
  const sum = (f) => live.reduce((s, b) => s + (Number(b[f]) || 0), 0);

  const exportCsv = () => {
    if (!bookings.length) return toast.error("Nothing to export");
    downloadCsv(csvFilename(filters), bookingsToCsv(bookings));
    toast.success(`Exported ${bookings.length} bookings`);
  };

  return (
    <div data-testid="bookings-page">
      <PageHeader title="Bookings" subtitle={loading ? "Loading…" : `${bookings.length} booking${bookings.length === 1 ? "" : "s"} match – totals below exclude cancelled jobs`}>
        <Button variant="outline" onClick={exportCsv} disabled={!bookings.length} data-testid="btn-export-csv"><Download className="h-4 w-4" /> Export CSV</Button>
        <LiveBadge updatedAt={updatedAt} />
        <Button variant="outline" onClick={() => navigate("/bookings/flagged")} data-testid="btn-fix-flagged" className="border-amber-300 text-amber-900 hover:bg-amber-50"><AlertTriangle className="h-4 w-4" /> Fix flagged</Button>
        <Button variant="outline" onClick={() => navigate("/bookings/import")} data-testid="btn-import-bookings"><Upload className="h-4 w-4" /> Import bookings</Button>
      </PageHeader>
      <BookingFilters filters={filters} setFilters={setFilters} extraLeadSources={bookings.map((b) => b.lead_source).filter(Boolean)} />
      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-5" data-testid="bookings-totals">
        <Total id="count" label="Jobs" value={live.length} />
        <Total id="price" label="Total job value" value={money(sum("total_estimate"))} />
        <Total id="deposits" label="Deposits paid" value={money(sum("deposit_amount"))} tone="text-emerald-700" />
        <Total id="extras" label="Driver extras" value={money(sum("driver_extras_total"))} />
        <Total id="balance" label="Outstanding" value={money(sum("remaining_balance"))} tone="text-amber-800" />
      </div>
      {loading && !data ? (
        <div className="py-20 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-400" /></div>
      ) : (
        <BookingTable bookings={bookings} actions={actions} emptyText="No bookings match. Try another search, or create a new booking." />
      )}
      {actions.dialog}
    </div>
  );
}
